import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import useListTruncator from 'hooks/useListTruncator'
import type { MouseEventHandler, ReactElement } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import SearchTileStackAmenitiesList from 'components/SearchKit/SearchTiles/shared/SearchTileStackAmenitiesList'

interface Props {
  features: Array<App.OfferInclusion>
  featuresType: 'inclusions' | 'amenities'
  cancellationPolicy?: ReactElement | false
  onMoreClick?: () => void
  singleColumn?: boolean
  /**
   * Extra hidden items rendered outside this list (e.g. loyalty inclusions)
   * that should be counted in the "+N more" link total.
   */
  additionalHiddenCount?: number
  maxInclusionsToShow?: number
}

export const MAX_INCLUSIONS_TO_SHOW = 6

function ExperimentalSearchTileFeaturesList({
  features,
  featuresType,
  cancellationPolicy,
  onMoreClick,
  singleColumn,
  additionalHiddenCount = 0,
  maxInclusionsToShow = MAX_INCLUSIONS_TO_SHOW,
}: Props) {
  const intl = useIntl()
  const [visibleInclusions, hiddenInclusions] = useListTruncator(maxInclusionsToShow - Number(!!cancellationPolicy), features)
  const handleMoreClick = useCallback<MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    onMoreClick?.()
  }, [onMoreClick])
  if (!cancellationPolicy && !visibleInclusions.length) return null

  return <Group direction="vertical" gap={4}>
    {cancellationPolicy}
    {featuresType === 'inclusions' && visibleInclusions.map(inclusion =>
      <OfferInclusionItem
        key={inclusion.id}
        inclusion={inclusion}
        lineClamp={2}
      />,
    )}
    {featuresType === 'inclusions' && !!(hiddenInclusions.length || additionalHiddenCount) && <div>
      {!!onMoreClick && <TextLink size="small" onClick={handleMoreClick}>{intl.formatMessage({
        id: 'JLiExn3LXJlJLEAWSb4J',
        defaultMessage: '+ {count} more',
        description: 'Link to show more truncated inclusions on search tile',
      }, { count: hiddenInclusions.length + additionalHiddenCount })}
      </TextLink>}
      {!onMoreClick && <BodyText variant="small" weight="bold">{intl.formatMessage({
        id: 'Itu8L9SiB1_uXFeRj72U',
        defaultMessage: '+ {count} more',
        description: 'Text showing count of additional truncated inclusions on search tile',
      }, { count: hiddenInclusions.length + additionalHiddenCount })}
      </BodyText>}
    </div>}
    {featuresType === 'amenities' &&
      <SearchTileStackAmenitiesList
        amenities={features}
        singleColumn={singleColumn}
      />}
  </Group>
}

export default ExperimentalSearchTileFeaturesList
